/**
 * src/lib/api/bundles.js — Project bundles client API
 */
import { apiFetch, API_BASE } from './client';
import { supabase } from '../supabase';

const BUNDLES = `${API_BASE}/data`;

// TICKET-977 S1b: bundle items are project_bundle_items rows. Direct reads embed them
// through the bundle_id FK (hinted: tasks links both tables too) and normalise to the
// shape bundles.list returns — `bundle_items` = live rows by position.
export const BUNDLE_WITH_ITEMS = '*, project_bundle_items!bundle_id(*)';
export function withBundleItems(row) {
  if (!row || !Object.prototype.hasOwnProperty.call(row, 'project_bundle_items')) return row;
  const { project_bundle_items: items, ...rest } = row;
  return {
    ...rest,
    bundle_items: (items || []).filter((i) => !i.archived_at).sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
  };
}

export async function fetchBundles(projectId) {
  const data = await apiFetch(`${BUNDLES}?action=bundles.list&project_id=${encodeURIComponent(projectId)}`);
  return { bundles: data?.bundles || [], crm_deal: data?.crm_deal || null };
}

// TICKET-703: bundle evolution feed — live-membership activity events
// (task/BOM line status changes) for a single bundle.
export async function fetchBundleActivityFeed(bundleId) {
  const data = await apiFetch(`${BUNDLES}?action=bundles.activity_feed&bundle_id=${encodeURIComponent(bundleId)}`);
  return data?.events || [];
}

export async function fetchAllBundles() {
  // Try API first, fall back to direct Supabase if API fails
  const data = await apiFetch(`${BUNDLES}?action=bundles.list&all=true`);
  if (data?.bundles?.length > 0) return { bundles: data.bundles };
  // Fallback: direct Supabase read (bypasses serverless function)
  try {
    const { data: rows, error } = await supabase
      .from('project_bundles')
      .select(BUNDLE_WITH_ITEMS)
      .order('created_at', { ascending: false });
    if (!error && rows?.length > 0) {
      console.log('[bundles] Using Supabase fallback:', rows.length, 'bundles');
      return { bundles: rows.map(withBundleItems) };
    }
  } catch (e) {
    console.warn('[bundles] Supabase fallback failed:', e.message);
  }
  return { bundles: data?.bundles || [] };
}

// TICKET-959: re-read ONE bundle (text, translations, items) after a translate,
// so the open panel shows the server row without waiting for the parent's reload.
export async function fetchBundleRow(id) {
  const { data, error } = await supabase.from('project_bundles').select(BUNDLE_WITH_ITEMS).eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return withBundleItems(data) || null;
}

export async function fetchBundlesByDealId(crmDealId) {
  const data = await apiFetch(`${BUNDLES}?action=bundles.list&crm_deal_id=${encodeURIComponent(crmDealId)}`);
  return { bundles: data?.bundles || [], crm_deal: data?.crm_deal || null };
}

export async function createBundle({ project_id, crm_deal_id, title, description, estimated_value, notes, origin, product_type, product_category, create_new_deal }) {
  return apiFetch(BUNDLES, {
    method: 'POST',
    body: JSON.stringify({ action: 'bundles.create', project_id, crm_deal_id, title, description, estimated_value, notes, origin, product_type, product_category, create_new_deal }),
  });
}

export async function updateBundle(id, fields) {
  return apiFetch(BUNDLES, {
    method: 'POST',
    body: JSON.stringify({ action: 'bundles.update', id, ...fields }),
  });
}

// TICKET-977 S3a: one item's quantity / measurement_raw; the server also syncs the
// quote lines linked to it (open quotes only). -> { success, item, sync }
export async function updateBundleItem(id, fields) {
  return apiFetch(BUNDLES, {
    method: 'POST',
    body: JSON.stringify({ action: 'bundles.item_update', id, ...fields }),
  });
}

// TICKET-977 S5: PMO item actions after WON. Each writes a bundle_item history row.
const itemPost = (action, body) => apiFetch(BUNDLES, { method: 'POST', body: JSON.stringify({ action, ...body }) });
/** -> { success, item } */
export const createBundleItem = (bundle_id, fields) => itemPost('bundles.item_create', { bundle_id, ...fields });
/** Soft archive. -> { success, item, changed } */
export const archiveBundleItem = (id) => itemPost('bundles.item_archive', { id });
/** -> { success, item, changed } */
export const restoreBundleItem = (id) => itemPost('bundles.item_restore', { id });
/** ids = every live item of the bundle in the new order. -> { success, items } */
export const reorderBundleItems = (bundle_id, ids) => itemPost('bundles.item_reorder', { bundle_id, ids });
/** Newest first: { entity_id, entity_name, action, old_value, new_value, actor_name, created_at }[] */
export async function fetchBundleItemHistory(bundleId) {
  const data = await apiFetch(`${BUNDLES}?action=bundles.item_history&bundle_id=${encodeURIComponent(bundleId)}`);
  return data?.events || [];
}

/** TICKET-977 S6 — quote vs execution for a project: { bundles: [...], totals } */
export async function fetchProjectDrift(projectId) {
  const data = await apiFetch(`${BUNDLES}?action=bundles.drift_report&project_id=${encodeURIComponent(projectId)}`);
  return { bundles: data?.bundles || [], totals: data?.totals || null };
}

export async function updateBundleStage(id, production_stage) {
  return apiFetch(BUNDLES, {
    method: 'POST',
    body: JSON.stringify({ action: 'bundles.update_stage', id, production_stage }),
  });
}

export async function deleteBundle(id) {
  return apiFetch(BUNDLES, {
    method: 'POST',
    body: JSON.stringify({ action: 'bundles.delete', id }),
  });
}
